import { useState, useMemo, forwardRef, useEffect, useRef } from "react";
// Sheet removed — Build Review now renders as a centered Dialog (matches Ordering/QuickAssign).
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Eye, CheckCircle, RotateCcw, Camera, Ruler, Package,
  AlertTriangle, X,
  ClipboardList, ShieldCheck, Layers,
  FileText, Wrench, Droplets, Box, Grip, Send, FileSignature,
  Phone, MapPin, User, Download,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Sparkles, Maximize2, Flag, Loader2,
} from "lucide-react";
import { getAttachments, createSignedAttachmentUrl } from "@/services/attachment.service";
import type { Job, Issue } from "@/data/types";
import type { RevisionReason, BuildReviewEvent, BuildReviewStatus } from "@/data/buildReviewTypes";
import { REVISION_REASON_LABELS, deriveBuildReviewStatus } from "@/data/buildReviewTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PushNextStepPanel, type PushNextStep } from "@/components/quick-actions/PushNextStepPanel";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { useProductRegistry } from "@/data/ProductRegistryContext";
import { supabase } from "@/lib/supabase";
import { isUuid } from "@/lib/uuid";
import { JobService } from "@/services/job.service";
import { BuildReviewService } from "@/services";
import { PhotoEstimationService } from "@/services/photo-estimation.service";
import { toast } from "sonner";
import { getRepByName } from "@/data/rosters/salesReps";
import { cn } from "@/lib/utils";
import { resolveImportPhotoUrls, IMPORT_PHOTO_BUCKET } from "@/data/import/importPhotoUrls";
import type { MigrationAiScopeDraft } from "@/data/import/migrationTypes";
import useEmblaCarousel from "embla-carousel-react";
import { ZoomPanLightbox } from "@/components/media/ZoomPanLightbox";


const SITE_PHOTOS_BUCKET = "job-documents";

interface SitePhoto {
  id: string;
  category: string;
  preview: string;
  name: string;
  capturedAt: string;
  source: "consultation" | "migration";
}


type ReviewLane = "needs-review" | "on-hold-sales" | "revision-required" | "awaiting-sales" | "awaiting-signature" | "resubmitted" | "final-review";

function PhotoSourceBadge({ source }: { source: "consultation" | "migration" }) {
  return (
    <span className={cn(
      "absolute top-1.5 left-1.5 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full backdrop-blur-sm",
      source === "migration"
        ? "bg-warning/90 text-warning-foreground"
        : "bg-primary/90 text-primary-foreground",
    )}>
      {source === "migration" ? "Import" : "Consult"}
    </span>
  );
}

interface IntakeReviewWorkspaceProps {
  job: Job | null;
  open: boolean;
  lane?: ReviewLane;
  buildReviewEvents?: BuildReviewEvent[];
  /** @deprecated Use buildReviewEvents */
  bathReviewEvents?: BuildReviewEvent[];
  onClose: () => void;
  onApprove: (jobId: string) => void;
  onRequestRevision: (jobId: string, notes: string, materials: string, reason: RevisionReason) => void;
  onSendToSales?: (jobId: string) => void;
  onSalesApprove?: (jobId: string) => void;
  onCustomerSigned?: (jobId: string) => void;
  onFinalApproval?: (jobId: string) => void;
  /** When provided, approval commits state but the workspace stays open and shows
   *  the Push-Mode "what's next" panel so the operator can chain into Generate PO,
   *  Permit, Crew, etc. Pair with onContinueChain to wire the chosen step. */
  onApproveAndContinue?: (jobId: string) => void;
  /** When provided alongside onApproveAndContinue, called when the operator picks
   *  a chained next step. Workspace closes after this fires. */
  onContinueChain?: (job: Job, step: PushNextStep) => void;
  /** Open issues (used to power getJobNextAction inside the chain panel). */
  openIssues?: Issue[];
}

const CHECKLIST_ITEMS = [
  { key: "measurements", label: "Measurements verified" },
  { key: "photos", label: "Photos match scope" },
  { key: "materials", label: "Materials match contract" },
  { key: "addons", label: "Add-ons confirmed" },
  { key: "components", label: "No missing components" },
  { key: "conditions", label: "Special conditions reviewed" },
];

function getProductLabel(job: Job): string {
  const material = job.projectConfig?.bathroomMaterials?.[0] ?? job.projectConfig?.primaryMaterial ?? "";
  const pt = job.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : job.projectType;
  return [material, pt].filter(Boolean).join(" ");
}

function getScopeSnapshotLine(job: Job): string {
  const cfg = job.projectConfig;
  if (!cfg) return job.projectType;
  const material = cfg.bathroomMaterials?.[0] || "";
  const enclosure = cfg.enclosureType || cfg.selectedGlass ? "Glass" : "";
  const parts = [material, "Shower", enclosure ? `• ${enclosure}` : ""].filter(Boolean);
  return parts.join(" ");
}

function getJobDisplayRef(job: Job): string {
  const primary = String((job as { jobNumber?: unknown }).jobNumber ?? "").trim();
  if (primary) return primary;
  const fallback = String(job.id || "").trim();
  if (!fallback) return "JOB-UNASSIGNED";
  return isUuid(fallback) ? `JOB ${fallback.slice(0, 8)}` : fallback;
}

/* ── Build package from POS config ── */
interface BuildComponent {
  category: string;
  icon: React.ReactNode;
  value: string;
  subItems?: string[];
  measurement?: string;
}

function resolveMeasurementForCategory(cfg: Job["projectConfig"], category: string): string | undefined {
  if (!cfg) return undefined;
  const wm = cfg.wallMeasurements;
  const wallParts = wm
    ? [
        wm.wallA?.width && wm.wallA?.height ? `A ${wm.wallA.width}"×${wm.wallA.height}"` : null,
        wm.wallB?.width && wm.wallB?.height ? `B ${wm.wallB.width}"×${wm.wallB.height}"` : null,
        (cfg.wallCount || 3) >= 3 && wm.wallC?.width && wm.wallC?.height ? `C ${wm.wallC.width}"×${wm.wallC.height}"` : null,
      ].filter(Boolean)
    : [];

  switch (category) {
    case "Wall System":
      return wallParts.length > 0 ? wallParts.join("  ·  ") : "—";
    case "Base / Pan":
      return cfg.baseWidth && cfg.baseLength ? `${cfg.baseWidth}" × ${cfg.baseLength}"` : "—";
    case "Enclosure": {
      const chips: string[] = [];
      if (cfg.hasWindow) chips.push("Window in space");
      if (cfg.obstructions?.length) chips.push(...cfg.obstructions.slice(0, 2));
      return chips.length > 0 ? chips.join("  ·  ") : undefined;
    }
    default:
      return undefined;
  }
}

function resolveOptionLabel(value: string | undefined, allOptions: { id: string; displayName: string }[]): string {
  if (!value) return "";
  return allOptions.find((o) => o.id === value)?.displayName || value;
}

function deriveBuildPackage(job: Job, allOptions: { id: string; displayName: string }[]): BuildComponent[] {
  const cfg = job.projectConfig;
  if (!cfg) return [];
  const items: BuildComponent[] = [];

  const resolvedWallSystem = (
    resolveOptionLabel(cfg.selectedWallSystem, allOptions).trim() ||
    cfg.bathroomMaterials?.[0] ||
    cfg.primaryMaterial ||
    cfg.bathroomSystemType ||
    ""
  );
  const resolvedBase = resolveOptionLabel(cfg.selectedBase, allOptions) || cfg.baseType || "";
  const resolvedEnclosure =
    cfg.enclosureType ||
    [resolveOptionLabel(cfg.selectedGlass, allOptions), resolveOptionLabel(cfg.selectedDoor, allOptions)].filter(Boolean).join(" + ") ||
    "";

  if (resolvedWallSystem)
    items.push({ category: "Wall System", icon: <Grip className="h-3.5 w-3.5" />, value: resolvedWallSystem, measurement: resolveMeasurementForCategory(cfg, "Wall System") });
  if (resolvedBase)
    items.push({ category: "Base / Pan", icon: <Box className="h-3.5 w-3.5" />, value: resolvedBase || "Standard", measurement: resolveMeasurementForCategory(cfg, "Base / Pan") });
  if (resolvedEnclosure)
    items.push({ category: "Enclosure", icon: <Layers className="h-3.5 w-3.5" />, value: resolvedEnclosure, measurement: resolveMeasurementForCategory(cfg, "Enclosure") });
  if (cfg.selectedGlass || cfg.selectedDoor) {
    const parts = [cfg.selectedGlass, cfg.selectedDoor].filter(Boolean);
    items.push({ category: "Glass / Door", icon: <Layers className="h-3.5 w-3.5" />, value: parts.join(" + "), subItems: parts.length > 1 ? parts : undefined });
  }
  if (cfg.selectedFixture)
    items.push({ category: "Fixture Package", icon: <Droplets className="h-3.5 w-3.5" />, value: resolveOptionLabel(cfg.selectedFixture, allOptions) || cfg.selectedFixture });
  if (cfg.selectedGrabBars?.length)
    items.push({
      category: "Hardware / Grab Bars",
      icon: <Wrench className="h-3.5 w-3.5" />,
      value: cfg.selectedGrabBars.map((id) => resolveOptionLabel(id, allOptions)).join(", "),
      subItems: cfg.selectedGrabBars.map((id) => resolveOptionLabel(id, allOptions)),
    });
  if (cfg.selectedAddOns?.length)
    items.push({
      category: "Add-ons",
      icon: <Package className="h-3.5 w-3.5" />,
      value: cfg.selectedAddOns.map((id) => resolveOptionLabel(id, allOptions)).join(", "),
      subItems: cfg.selectedAddOns.map((id) => resolveOptionLabel(id, allOptions)),
    });
  if (cfg.plumbingRelocation)
    items.push({ category: "Prep / Waterproofing", icon: <Droplets className="h-3.5 w-3.5" />, value: "Plumbing relocation required" });
  if (cfg.customModifications)
    items.push({ category: "Custom Modifications", icon: <Wrench className="h-3.5 w-3.5" />, value: "Custom modifications included" });
  return items;
}

/* ── System validation checks ── */
interface ValidationIssue {
  type: "error" | "warning";
  message: string;
}

/* ── ShowerFloorPlan — interactive top-down SVG floor plan ── */
function ShowerFloorPlan({ cfg, approximate = false }: { cfg: NonNullable<Job["projectConfig"]>; approximate?: boolean }) {
  const bW = Number(cfg.baseWidth ?? 0);
  const bL = Number(cfg.baseLength ?? 0);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!canvasEl) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom(z => Math.max(0.5, Math.min(3, z + (e.deltaY < 0 ? 0.15 : -0.15))));
    };
    canvasEl.addEventListener("wheel", handler, { passive: false });
    return () => canvasEl.removeEventListener("wheel", handler);
  }, [canvasEl]);

  if (!bW || !bL) return null;

  const WALL_T = 8;
  const PAD_X = 56;
  const PAD_Y = 40;
  const SVG_W = 440;
  const SVG_H = 320;

  const planW = SVG_W - PAD_X * 2;
  const planH = SVG_H - PAD_Y * 2;
  const scale = Math.min(planW / (bW + WALL_T * 2), planH / (bL + WALL_T)) * 1.05;

  const sw = bW * scale;
  const sl = bL * scale;
  const st = WALL_T * scale;

  const ox = (SVG_W - (sw + st * 2)) / 2;
  const oy = PAD_Y;

  const wm = cfg.wallMeasurements;
  const wallCount = cfg.wallCount ?? 3;

  const drainLoc = cfg.drainLocation ?? "";
  const drainXRatio = drainLoc.includes("left") ? 0.25 : drainLoc.includes("right") ? 0.75 : 0.5;
  const drainYRatio = drainLoc.includes("front") ? 0.25 : drainLoc.includes("back") ? 0.75 : 0.5;
  const drainX = ox + st + sw * drainXRatio;
  const drainY = oy + st + sl * drainYRatio;

  // Tile grid spacing (cosmetic — suggests ~12" tiles at plan scale)
  const tileSize = 12 * scale;

  const dimStyle: React.CSSProperties = { fontSize: 9, fill: "var(--color-muted-foreground, #aaa)", fontFamily: "inherit", fontVariantNumeric: "tabular-nums" };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    setPan({ x: dragRef.current.panX + e.clientX - dragRef.current.startX, y: dragRef.current.panY + e.clientY - dragRef.current.startY });
  };
  const handleMouseUp = () => { dragRef.current = null; };

  // Dimension line helper — renders a line with tick caps and centered label
  const DimLine = ({ x1, y1, x2, y2, label, offset = 0, vertical = false }: { x1: number; y1: number; x2: number; y2: number; label: string; offset?: number; vertical?: boolean }) => {
    const tick = 4;
    const tx = vertical ? offset : 0;
    const ty = vertical ? 0 : offset;
    return (
      <g>
        <line x1={x1 + tx} y1={y1 + ty} x2={x2 + tx} y2={y2 + ty} stroke="var(--color-muted-foreground, #666)" strokeWidth={0.5} strokeDasharray="2 2" opacity={0.5} />
        {vertical ? (
          <>
            <line x1={x1 + tx - tick} y1={y1 + ty} x2={x1 + tx + tick} y2={y1 + ty} stroke="var(--color-muted-foreground, #666)" strokeWidth={0.5} opacity={0.5} />
            <line x1={x2 + tx - tick} y1={y2 + ty} x2={x2 + tx + tick} y2={y2 + ty} stroke="var(--color-muted-foreground, #666)" strokeWidth={0.5} opacity={0.5} />
          </>
        ) : (
          <>
            <line x1={x1 + tx} y1={y1 + ty - tick} x2={x1 + tx} y2={y1 + ty + tick} stroke="var(--color-muted-foreground, #666)" strokeWidth={0.5} opacity={0.5} />
            <line x1={x2 + tx} y1={y2 + ty - tick} x2={x2 + tx} y2={y2 + ty + tick} stroke="var(--color-muted-foreground, #666)" strokeWidth={0.5} opacity={0.5} />
          </>
        )}
        <text
          x={(x1 + x2) / 2 + tx}
          y={(y1 + y2) / 2 + ty}
          textAnchor="middle"
          dominantBaseline="middle"
          style={dimStyle}
        >
          <tspan dx={0} dy={0} style={{ fill: "var(--color-background, #111)", stroke: "var(--color-background, #111)", strokeWidth: 3, paintOrder: "stroke" }}>{label}</tspan>
          <tspan dx={0} dy={0} x={(x1 + x2) / 2 + tx} style={{ fill: "var(--color-muted-foreground, #aaa)" }}>{label}</tspan>
        </text>
      </g>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between shrink-0">
        <p className="text-[9px] uppercase tracking-wider text-muted-foreground/50 font-semibold flex items-center gap-1">
          <Ruler className="h-2.5 w-2.5" /> Floor Plan (top-down){approximate && <span className="text-warning/60 ml-1">· AI-estimated</span>}
        </p>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom(z => Math.min(3, z + 0.2))} className="h-5 w-5 rounded text-[9px] bg-muted/40 hover:bg-muted text-muted-foreground flex items-center justify-center">+</button>
          <button onClick={() => setZoom(z => Math.max(0.5, z - 0.2))} className="h-5 w-5 rounded text-[9px] bg-muted/40 hover:bg-muted text-muted-foreground flex items-center justify-center">-</button>
          <button onClick={() => setRotation(r => (r + 90) % 360)} className="h-5 px-1.5 rounded text-[9px] bg-muted/40 hover:bg-muted text-muted-foreground flex items-center gap-0.5">
            <RotateCcw className="h-2.5 w-2.5" /> {rotation}°
          </button>
          <button onClick={() => { setPan({ x: 0, y: 0 }); setZoom(1); setRotation(0); }} className="h-5 px-1.5 rounded text-[9px] bg-muted/40 hover:bg-muted text-muted-foreground">Reset</button>
        </div>
      </div>

      {/* Canvas — fills all remaining vertical space */}
      <div
        ref={setCanvasEl}
        className="rounded-lg overflow-hidden bg-muted/10 border border-border/50 flex-1 min-h-0"
        style={{ cursor: dragRef.current ? "grabbing" : "grab", userSelect: "none" }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <svg
          viewBox={`0 0 ${SVG_W} ${SVG_H}`}
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          style={{ transform: `rotate(${rotation}deg) scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`, transformOrigin: "center", transition: dragRef.current ? "none" : "transform 0.15s ease" }}
        >
          <defs>
            {/* Tile grid pattern */}
            <pattern id="tileGrid" width={tileSize} height={tileSize} patternUnits="userSpaceOnUse" patternTransform={`translate(${ox + st},${oy + st})`}>
              <rect width={tileSize} height={tileSize} fill="none" stroke="var(--color-muted-foreground, #555)" strokeWidth={0.3} opacity={0.15} />
            </pattern>
            {/* Subtle wall texture */}
            <linearGradient id="wallAGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
            </linearGradient>
            <linearGradient id="wallBGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.12} />
            </linearGradient>
            <linearGradient id="wallCGrad" x1="1" y1="0" x2="0" y2="0">
              <stop offset="0%" stopColor="#10b981" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#10b981" stopOpacity={0.12} />
            </linearGradient>
            {/* Drain radial */}
            <radialGradient id="drainGrad">
              <stop offset="0%" stopColor="var(--color-muted-foreground, #888)" stopOpacity={0.4} />
              <stop offset="100%" stopColor="var(--color-muted-foreground, #888)" stopOpacity={0.05} />
            </radialGradient>
          </defs>

          {/* Background dot grid */}
          {Array.from({ length: Math.floor(SVG_W / 20) + 1 }, (_, i) =>
            Array.from({ length: Math.floor(SVG_H / 20) + 1 }, (_, j) => (
              <circle key={`dot-${i}-${j}`} cx={i * 20} cy={j * 20} r={0.4} fill="var(--color-muted-foreground, #555)" opacity={0.12} />
            ))
          )}

          {/* Floor area with tile grid */}
          <rect x={ox + st} y={oy + st} width={sw} height={sl} rx={1.5} fill="var(--color-muted, #1a1a2e)" opacity={0.35} />
          <rect x={ox + st} y={oy + st} width={sw} height={sl} rx={1.5} fill="url(#tileGrid)" />
          <rect x={ox + st} y={oy + st} width={sw} height={sl} rx={1.5} fill="none" stroke="var(--color-border, #333)" strokeWidth={0.5} />

          {/* Wall A — back (gradient fill) */}
          <rect x={ox + st} y={oy} width={sw} height={st} rx={1.5} fill="url(#wallAGrad)" />
          <rect x={ox + st} y={oy} width={sw} height={st} rx={1.5} fill="none" stroke="hsl(var(--primary))" strokeWidth={0.75} opacity={0.6} />
          {/* Wall B — left */}
          <rect x={ox} y={oy} width={st} height={sl + st} rx={1.5} fill="url(#wallBGrad)" />
          <rect x={ox} y={oy} width={st} height={sl + st} rx={1.5} fill="none" stroke="#3b82f6" strokeWidth={0.75} opacity={0.5} />
          {/* Wall C — right */}
          {wallCount >= 3 && (
            <>
              <rect x={ox + st + sw} y={oy} width={st} height={sl + st} rx={1.5} fill="url(#wallCGrad)" />
              <rect x={ox + st + sw} y={oy} width={st} height={sl + st} rx={1.5} fill="none" stroke="#10b981" strokeWidth={0.75} opacity={0.5} />
            </>
          )}

          {/* Corner joints — small squares where walls meet */}
          <rect x={ox} y={oy} width={st} height={st} rx={1} fill="var(--color-muted-foreground, #666)" opacity={0.12} />
          {wallCount >= 3 && <rect x={ox + st + sw} y={oy} width={st} height={st} rx={1} fill="var(--color-muted-foreground, #666)" opacity={0.12} />}

          {/* Open front — glass door indicator */}
          <line x1={ox + st} y1={oy + st + sl} x2={ox + st + sw} y2={oy + st + sl}
            stroke="var(--color-muted-foreground, #666)" strokeWidth={1.5} strokeDasharray="6 4" opacity={0.35} />
          {/* Glass door arc on left side of entry */}
          <path
            d={`M ${ox + st} ${oy + st + sl} A ${sw * 0.35} ${sw * 0.35} 0 0 1 ${ox + st + sw * 0.35} ${oy + st + sl + sw * 0.12}`}
            fill="none" stroke="var(--color-primary, #60a5fa)" strokeWidth={1} opacity={0.3} strokeDasharray="3 2"
          />
          <rect x={ox + st} y={oy + st + sl - 1} width={sw * 0.35} height={2} rx={1} fill="var(--color-primary, #60a5fa)" opacity={0.25} />

          {/* Drain — enhanced radial icon */}
          {cfg.drainLocation && (
            <g>
              <circle cx={drainX} cy={drainY} r={8} fill="url(#drainGrad)" />
              <circle cx={drainX} cy={drainY} r={5} fill="none" stroke="var(--color-muted-foreground, #666)" strokeWidth={0.5} opacity={0.4} />
              {[0, 45, 90, 135].map(a => (
                <line key={a}
                  x1={drainX + Math.cos(a * Math.PI / 180) * 2}
                  y1={drainY + Math.sin(a * Math.PI / 180) * 2}
                  x2={drainX + Math.cos(a * Math.PI / 180) * 4.5}
                  y2={drainY + Math.sin(a * Math.PI / 180) * 4.5}
                  stroke="var(--color-muted-foreground, #666)" strokeWidth={0.5} opacity={0.3}
                />
              ))}
              <circle cx={drainX} cy={drainY} r={1.5} fill="var(--color-muted-foreground, #888)" opacity={0.5} />
            </g>
          )}

          {/* Wall labels — badge style */}
          {[
            { label: "A", x: ox + st + sw / 2, y: oy + st / 2, color: "hsl(var(--primary))" },
            { label: "B", x: ox + st / 2, y: oy + st + sl / 2, color: "#3b82f6" },
            ...(wallCount >= 3 ? [{ label: "C", x: ox + st + sw + st / 2, y: oy + st + sl / 2, color: "#10b981" }] : []),
          ].map(w => (
            <g key={w.label}>
              <circle cx={w.x} cy={w.y} r={7} fill={w.color} opacity={0.15} />
              <text x={w.x} y={w.y} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 8, fill: w.color, fontWeight: 700, fontFamily: "inherit" }}>{w.label}</text>
            </g>
          ))}

          {/* Center base label */}
          <text x={ox + st + sw / 2} y={oy + st + sl / 2} textAnchor="middle" dominantBaseline="central" style={{ fontSize: 12, fill: "var(--color-foreground, #eee)", fontWeight: 600, fontFamily: "inherit", fontVariantNumeric: "tabular-nums" }}>
            {bW}" × {bL}"
          </text>

          {/* ── Dimension lines with tick caps ── */}
          {/* Wall A — top */}
          <DimLine x1={ox + st} y1={oy - 14} x2={ox + st + sw} y2={oy - 14} label={`${wm?.wallA?.width ?? bW}"`} />
          {/* Wall B — left */}
          <DimLine x1={ox - 18} y1={oy + st} x2={ox - 18} y2={oy + st + sl} label={`${wm?.wallB?.width ?? bL}"`} vertical offset={0} />
          {/* Wall C — right */}
          {wallCount >= 3 && (
            <DimLine x1={ox + st * 2 + sw + 18} y1={oy + st} x2={ox + st * 2 + sw + 18} y2={oy + st + sl} label={`${wm?.wallC?.width ?? bL}"`} vertical offset={0} />
          )}

          {/* Wall heights — small sub-labels */}
          {wm?.wallA?.height && (
            <text x={ox + st + sw / 2} y={oy + st / 2 + 10} textAnchor="middle" dominantBaseline="middle" style={{ ...dimStyle, fontSize: 7, opacity: 0.5 }}>h: {wm.wallA.height}"</text>
          )}
          {wm?.wallB?.height && (
            <text x={ox + st / 2} y={oy + st + sl / 2 + 12} textAnchor="middle" dominantBaseline="middle" style={{ ...dimStyle, fontSize: 7, opacity: 0.5 }}>h: {wm.wallB.height}"</text>
          )}
          {wm?.wallC?.height && wallCount >= 3 && (
            <text x={ox + st + sw + st / 2} y={oy + st + sl / 2 + 12} textAnchor="middle" dominantBaseline="middle" style={{ ...dimStyle, fontSize: 7, opacity: 0.5 }}>h: {wm.wallC.height}"</text>
          )}

          {/* Entry label + arrow */}
          <text x={ox + st + sw / 2} y={oy + st + sl + 20} textAnchor="middle" style={{ ...dimStyle, fontSize: 8, letterSpacing: "0.5px" }}>
            ENTRY
          </text>
          <path d={`M ${ox + st + sw / 2} ${oy + st + sl + 12} L ${ox + st + sw / 2 - 3} ${oy + st + sl + 16} M ${ox + st + sw / 2} ${oy + st + sl + 12} L ${ox + st + sw / 2 + 3} ${oy + st + sl + 16}`}
            stroke="var(--color-muted-foreground, #888)" strokeWidth={0.75} fill="none" opacity={0.5} />

          {/* Legend — bottom-right */}
          <g transform={`translate(${SVG_W - 70}, ${SVG_H - 42})`} opacity={0.5}>
            <rect x={0} y={0} width={4} height={4} rx={0.5} fill="hsl(var(--primary))" opacity={0.6} />
            <text x={7} y={3.5} style={{ fontSize: 6.5, fill: "var(--color-muted-foreground, #888)", fontFamily: "inherit" }}>Wall A</text>
            <rect x={0} y={8} width={4} height={4} rx={0.5} fill="#3b82f6" opacity={0.5} />
            <text x={7} y={11.5} style={{ fontSize: 6.5, fill: "var(--color-muted-foreground, #888)", fontFamily: "inherit" }}>Wall B</text>
            {wallCount >= 3 && (
              <>
                <rect x={0} y={16} width={4} height={4} rx={0.5} fill="#10b981" opacity={0.5} />
                <text x={7} y={19.5} style={{ fontSize: 6.5, fill: "var(--color-muted-foreground, #888)", fontFamily: "inherit" }}>Wall C</text>
              </>
            )}
            {cfg.drainLocation && (
              <>
                <circle cx={2} cy={wallCount >= 3 ? 26 : 18} r={2} fill="var(--color-muted-foreground, #888)" opacity={0.4} />
                <text x={7} y={wallCount >= 3 ? 27.5 : 19.5} style={{ fontSize: 6.5, fill: "var(--color-muted-foreground, #888)", fontFamily: "inherit" }}>Drain</text>
              </>
            )}
          </g>

          {/* Scale bar — bottom-left */}
          {(() => {
            const scaleBarInches = 12; // 1 foot reference
            const scaleBarPx = scaleBarInches * scale;
            const sbX = 12;
            const sbY = SVG_H - 14;
            return (
              <g opacity={0.4}>
                <line x1={sbX} y1={sbY} x2={sbX + scaleBarPx} y2={sbY} stroke="var(--color-muted-foreground, #888)" strokeWidth={1} />
                <line x1={sbX} y1={sbY - 3} x2={sbX} y2={sbY + 3} stroke="var(--color-muted-foreground, #888)" strokeWidth={0.75} />
                <line x1={sbX + scaleBarPx} y1={sbY - 3} x2={sbX + scaleBarPx} y2={sbY + 3} stroke="var(--color-muted-foreground, #888)" strokeWidth={0.75} />
                <text x={sbX + scaleBarPx / 2} y={sbY - 5} textAnchor="middle" style={{ fontSize: 6, fill: "var(--color-muted-foreground, #888)", fontFamily: "inherit" }}>1 ft</text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}

function deriveAiScopeSummaryCards(draft: MigrationAiScopeDraft): BuildComponent[] {
  const items: BuildComponent[] = [];
  if (draft.materialsAndSelections?.length) {
    items.push({ category: "Materials & Selections", icon: <Package className="h-3.5 w-3.5" />, value: draft.materialsAndSelections[0], subItems: draft.materialsAndSelections.slice(1) });
  }
  if (draft.colorSelections?.length) {
    items.push({ category: "Colors & Finishes", icon: <Grip className="h-3.5 w-3.5" />, value: draft.colorSelections[0], subItems: draft.colorSelections.slice(1) });
  }
  if (draft.panelBreakdown?.length) {
    items.push({ category: "Panel Breakdown", icon: <Layers className="h-3.5 w-3.5" />, value: draft.panelBreakdown[0], subItems: draft.panelBreakdown.slice(1) });
  }
  if (draft.installerScopeItems?.length) {
    items.push({ category: "Installer Scope", icon: <Wrench className="h-3.5 w-3.5" />, value: draft.installerScopeItems[0], subItems: draft.installerScopeItems.slice(1) });
  }
  if (draft.fieldNotes?.length) {
    items.push({ category: "Field Notes", icon: <FileText className="h-3.5 w-3.5" />, value: draft.fieldNotes[0], subItems: draft.fieldNotes.slice(1) });
  }
  return items;
}

function deriveValidationIssues(job: Job, allOptions: { id: string; displayName: string }[]): ValidationIssue[] {
  const cfg = job.projectConfig;
  if (!cfg) return [{ type: "error", message: "No project configuration found" }];

  const issues: ValidationIssue[] = [];
  const resolvedWallSystem = (
    resolveOptionLabel(cfg.selectedWallSystem, allOptions).trim() ||
    cfg.bathroomMaterials?.[0] ||
    cfg.primaryMaterial ||
    cfg.bathroomSystemType ||
    ""
  );
  const resolvedBase = cfg.selectedBase || cfg.baseType || "";
  const resolvedEnclosure =
    cfg.enclosureType ||
    [cfg.selectedGlass, cfg.selectedDoor].filter(Boolean).join(" + ") ||
    "";
  const resolvedFixture = cfg.selectedFixture || cfg.hardwareFinish || "";

  // Missing components
  if (!resolvedWallSystem) issues.push({ type: "error", message: "Missing: Wall System not defined" });
  if (!resolvedBase) issues.push({ type: "error", message: "Missing: Base / Pan not defined" });
  if (!resolvedEnclosure) issues.push({ type: "error", message: "Missing: Enclosure type not defined" });
  if (!resolvedFixture) issues.push({ type: "warning", message: "Missing: Fixture package not selected" });

  // Incompatible selections
  if (cfg.bathroomMaterials?.[0] && resolvedWallSystem) {
    const soldMat = cfg.bathroomMaterials[0].toLowerCase();
    const wallMat = resolvedWallSystem.toLowerCase();
    if (!wallMat.includes(soldMat.split(" ")[0])) {
      issues.push({ type: "warning", message: `Possible mismatch: Sold material "${cfg.bathroomMaterials[0]}" vs wall system "${resolvedWallSystem}"` });
    }
  }

  // Measurement conflicts
  if (!cfg.baseWidth || !cfg.baseLength) {
    issues.push({ type: "warning", message: "Base dimensions not specified" });
  }
  const wm = cfg.wallMeasurements;
  if (wm) {
    if (!wm.wallA?.width || !wm.wallA?.height) issues.push({ type: "warning", message: "Wall A measurements incomplete" });
    if (!wm.wallB?.width || !wm.wallB?.height) issues.push({ type: "warning", message: "Wall B measurements incomplete" });
    if ((cfg.wallCount || 3) >= 3 && (!wm.wallC?.width || !wm.wallC?.height)) {
      issues.push({ type: "warning", message: "Wall C measurements incomplete for 3-wall layout" });
    }
  } else {
    issues.push({ type: "warning", message: "No per-wall measurements recorded" });
  }

  // Structural consistency
  if (cfg.structuralChanges) {
    if (cfg.structuralChanges.movingDrain && !cfg.plumbingRelocation) {
      issues.push({ type: "warning", message: "Drain relocation marked but plumbing relocation not flagged" });
    }
  }

  if (cfg.measurementNotes?.includes("MISMATCH") || cfg.measurementNotes?.toLowerCase().includes("conflict")) {
    issues.push({ type: "error", message: "Measurement notes contain conflict flag" });
  }

  return issues;
}

/* ── AiScopeCard — rich AI-generated scope for imported jobs ── */
function AiScopeCard({ draft }: { draft: MigrationAiScopeDraft }) {
  const [open, setOpen] = useState<Record<string, boolean>>({
    colors: true,
    panel: true,
    scope: false,
    materials: false,
    warnings: true,
    missing: true,
  });

  const toggle = (key: string) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const confidencePct = Math.round((draft.confidence ?? 0) * 100);
  const isComplete = (draft.warnings?.length ?? 0) === 0 && (draft.missingFields?.length ?? 0) === 0;

  interface Section {
    key: string;
    label: string;
    items: string[];
    tone: "default" | "warn" | "bad";
  }

  const sections: Section[] = [
    { key: "colors", label: "Colors & Finishes", items: draft.colorSelections ?? [], tone: "default" },
    { key: "panel", label: "Panel Breakdown", items: draft.panelBreakdown ?? [], tone: "default" },
    { key: "scope", label: "Installer Scope", items: draft.installerScopeItems ?? [], tone: "default" },
    { key: "warnings", label: `Warnings (${draft.warnings?.length ?? 0})`, items: draft.warnings ?? [], tone: "warn" },
    { key: "missing", label: `Missing Fields (${draft.missingFields?.length ?? 0})`, items: draft.missingFields ?? [], tone: "bad" },
  ].filter((s) => s.items.length > 0);

  return (
    <Card className="border-primary/20 bg-primary/[0.02] overflow-hidden">
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">AI Installer Scope</h3>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isComplete ? (
              <Badge className="text-[9px] h-4 bg-success/15 text-success border-0">Scope complete</Badge>
            ) : null}
            <Badge variant="outline" className="text-[9px] h-4 tabular-nums">{confidencePct}% confidence</Badge>
            {draft.model ? (
              <Badge variant="outline" className="text-[9px] h-4 text-muted-foreground/60">{draft.model}</Badge>
            ) : null}
          </div>
        </div>

        {/* Primary scope summary */}
        <p className="text-[14px] font-medium text-foreground leading-snug whitespace-pre-line">
          {draft.scopeSummary}
        </p>

        {/* Generated date */}
        {draft.generatedAt ? (
          <p className="text-[10px] text-muted-foreground/50">
            Generated {new Date(draft.generatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
            {draft.contractFileName ? ` · ${draft.contractFileName}` : ""}
          </p>
        ) : null}

        {/* Extracted materials */}
        {(draft.extractedMaterials?.length ?? 0) > 0 && (
          <div className="border-t border-border pt-2">
            <button
              onClick={() => toggle("materials")}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                Extracted Materials ({draft.extractedMaterials!.length})
              </span>
              {open.materials ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
            </button>
            {open.materials && (
              <div className="mt-1.5 space-y-0.5">
                {draft.extractedMaterials!.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px]">
                    <span className="text-foreground">{m.itemName}</span>
                    <span className="text-muted-foreground tabular-nums shrink-0 ml-2">
                      ×{m.quantity} · {Math.round((m.confidence ?? 0) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Collapsible sections */}
        {sections.map((section) => (
          <div key={section.key} className="border-t border-border pt-2">
            <button
              onClick={() => toggle(section.key)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className={cn(
                "text-[10px] font-semibold uppercase tracking-wider",
                section.tone === "warn" ? "text-amber-600 dark:text-amber-400" :
                section.tone === "bad" ? "text-destructive" :
                "text-muted-foreground"
              )}>
                {section.label}
              </span>
              {open[section.key] ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
            </button>
            {open[section.key] && (
              <ul className="mt-1.5 space-y-0.5">
                {section.items.map((item, i) => (
                  <li key={i} className={cn(
                    "text-[11px] pl-2 border-l-2",
                    section.tone === "warn" ? "border-amber-400/40 text-amber-700 dark:text-amber-300" :
                    section.tone === "bad" ? "border-destructive/40 text-destructive" :
                    "border-border text-foreground"
                  )}>
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ── SitePhotoCarousel — unified Embla carousel with filter chips + thumbnails ── */
const CATEGORY_META: Array<{ key: string; label: string; color: string }> = [
  { key: "project_area", label: "Project Area", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  { key: "measurement", label: "Measurement", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
  { key: "obstruction", label: "Obstruction", color: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  { key: "imported", label: "Imported", color: "bg-warning/15 text-warning" },
];

function getCategoryMeta(category: string) {
  return CATEGORY_META.find((m) => m.key === category) ?? { key: category, label: category, color: "bg-muted text-muted-foreground" };
}

type PhotoEstimations = Record<number, { loading: boolean; result: string | null }>;

interface SitePhotoCarouselProps {
  photos: SitePhoto[];
  onFlag: (photoIndex: number, category: string) => void;
  onExpand: (photoIndex: number) => void;
  /** When set, carousel jumps to first photo of this category */
  jumpToCategory?: string | null;
  onJumpHandled?: () => void;
  estimations: PhotoEstimations;
  onEstimate: (index: number, imageUrl: string) => void;
}

function SitePhotoCarousel({ photos, onFlag, onExpand, jumpToCategory, onJumpHandled, estimations, onEstimate }: SitePhotoCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [thumbEmblaRef, thumbEmblaApi] = useEmblaCarousel({ loop: false, align: "start", containScroll: "keepSnaps" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState<string>("all");

  // Sync selected index from Embla
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  // Sync thumbnail strip to main carousel
  useEffect(() => {
    if (!thumbEmblaApi) return;
    thumbEmblaApi.scrollTo(selectedIndex);
  }, [selectedIndex, thumbEmblaApi]);

  // Jump to first photo of category when filter changes
  useEffect(() => {
    if (!emblaApi || activeFilter === "all") return;
    const idx = photos.findIndex((p) => p.category === activeFilter);
    if (idx !== -1) emblaApi.scrollTo(idx);
  }, [activeFilter, emblaApi, photos]);

  // External jump-to-category from summary card clicks
  useEffect(() => {
    if (!jumpToCategory || !emblaApi) return;
    const idx = photos.findIndex((p) => p.category === jumpToCategory);
    if (idx !== -1) {
      emblaApi.scrollTo(idx);
      setSelectedIndex(idx);
    }
    onJumpHandled?.();
  }, [jumpToCategory, emblaApi, photos, onJumpHandled]);

  const scrollTo = (idx: number) => emblaApi?.scrollTo(idx);
  const categories = Array.from(new Set(photos.map((p) => p.category)));
  const photo = photos[selectedIndex];

  if (photos.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-[11px] text-muted-foreground">
        No site photos available — uploaded during the consultation or imported with the job.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Category filter chips */}
      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setActiveFilter("all")}
          className={cn(
            "text-[10px] px-2 py-0.5 rounded-full border transition-colors",
            activeFilter === "all" ? "bg-primary/15 text-primary border-primary/30" : "border-border text-muted-foreground hover:bg-accent/40"
          )}
        >
          All ({photos.length})
        </button>
        {CATEGORY_META.filter((m) => categories.includes(m.key)).map((m) => {
          const count = photos.filter((p) => p.category === m.key).length;
          return (
            <button
              key={m.key}
              onClick={() => setActiveFilter(m.key)}
              className={cn(
                "text-[10px] px-2 py-0.5 rounded-full border transition-colors",
                activeFilter === m.key ? "bg-primary/15 text-primary border-primary/30" : "border-border text-muted-foreground hover:bg-accent/40"
              )}
            >
              {m.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Main carousel */}
      <div className="relative rounded-lg overflow-hidden bg-muted/20 border border-border" style={{ height: 340 }}>
        <div className="overflow-hidden h-full" ref={emblaRef}>
          <div className="flex h-full">
            {photos.map((p, i) => (
              <div key={p.id} className="relative flex-[0_0_100%] h-full flex items-center justify-center">
                <img
                  src={p.preview}
                  alt={p.name}
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                  loading={Math.abs(i - selectedIndex) <= 1 ? "eager" : "lazy"}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Category badge + Estimate button */}
        {photo && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
            <span className={cn("text-[9px] font-bold px-2 py-0.5 rounded-full pointer-events-none", getCategoryMeta(photo.category).color)}>
              {getCategoryMeta(photo.category).label}
            </span>
            <button
              onClick={() => onEstimate(selectedIndex, photo.preview)}
              disabled={estimations[selectedIndex]?.loading}
              className={cn(
                "inline-flex items-center gap-1 text-[9px] font-semibold px-2 py-0.5 rounded-full transition-colors",
                photo.category === "measurement"
                  ? "bg-primary/80 text-white hover:bg-primary"
                  : "bg-black/50 text-white hover:bg-black/80",
                estimations[selectedIndex]?.loading && "opacity-60 cursor-not-allowed"
              )}
              title="Estimate dimensions with Gemini Vision"
            >
              {estimations[selectedIndex]?.loading
                ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                : <Ruler className="h-2.5 w-2.5" />}
              {estimations[selectedIndex]?.loading ? "Estimating…" : "Estimate"}
            </button>
          </div>
        )}

        {/* Photo counter + expand */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          <span className="text-[10px] tabular-nums bg-black/50 text-white px-2 py-0.5 rounded-full">
            {selectedIndex + 1} / {photos.length}
          </span>
          <button
            onClick={() => onExpand(selectedIndex)}
            className="h-6 w-6 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
            title="Expand / zoom"
          >
            <Maximize2 className="h-3 w-3" />
          </button>
        </div>

        {/* Prev arrow */}
        {selectedIndex > 0 && (
          <button
            onClick={() => scrollTo(selectedIndex - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        {/* Next arrow */}
        {selectedIndex < photos.length - 1 && (
          <button
            onClick={() => scrollTo(selectedIndex + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Estimation result card */}
      {photo && estimations[selectedIndex]?.result && (
        <div className="rounded-md border border-primary/20 bg-primary/[0.03] px-3 py-2 space-y-0.5">
          <p className="text-[9px] font-semibold text-primary/70 flex items-center gap-1 uppercase tracking-wide">
            <Ruler className="h-2.5 w-2.5" /> OpenAI Estimate
          </p>
          <p className="text-[12px] text-foreground font-medium">{estimations[selectedIndex].result}</p>
          <p className="text-[9px] text-muted-foreground/50">Measurements are approximate</p>
        </div>
      )}

      {/* Flag button */}
      {photo && (
        <button
          onClick={() => onFlag(selectedIndex, photo.category)}
          className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-destructive transition-colors"
        >
          <Flag className="h-3 w-3" />
          Flag this photo
        </button>
      )}

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div className="overflow-hidden" ref={thumbEmblaRef}>
          <div className="flex gap-1.5">
            {photos.map((p, i) => (
              <button
                key={p.id}
                onClick={() => scrollTo(i)}
                className={cn(
                  "flex-[0_0_64px] h-12 rounded overflow-hidden border-2 transition-all shrink-0",
                  i === selectedIndex ? "border-primary" : "border-transparent opacity-60 hover:opacity-100"
                )}
              >
                <img src={p.preview} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const IntakeReviewWorkspace = forwardRef<HTMLDivElement, IntakeReviewWorkspaceProps>(function IntakeReviewWorkspace({ job, open, lane = "needs-review", buildReviewEvents, bathReviewEvents, onClose, onApprove, onRequestRevision, onSendToSales, onSalesApprove, onCustomerSigned, onFinalApproval, onApproveAndContinue, onContinueChain, openIssues = [] }, _ref) {
  // Note: reviewEvents (buildReviewEvents/bathReviewEvents) intentionally
  // unused on this surface — JobActivityTimeline was removed (see note below).
  // Lane derivation reads them directly inside the useMemo below.
  const [salesRepDisplayName, setSalesRepDisplayName] = useState("");
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [revisionNotes, setRevisionNotes] = useState("");
  const [revisionMaterials, setRevisionMaterials] = useState("");
  const [revisionReason, setRevisionReason] = useState<RevisionReason | "">("");
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [expandedPhoto, setExpandedPhoto] = useState<number | null>(null);
  const [sitePhotos, setSitePhotos] = useState<SitePhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [photoEstimations, setPhotoEstimations] = useState<PhotoEstimations>({});
  const [mobileTab, setMobileTab] = useState<"spec" | "photos" | "review">("photos");

  const handleEstimatePhoto = async (index: number, imageUrl: string) => {
    setPhotoEstimations(prev => ({ ...prev, [index]: { loading: true, result: null } }));
    const { result, error } = await PhotoEstimationService.estimate(imageUrl);
    setPhotoEstimations(prev => ({
      ...prev,
      [index]: { loading: false, result: result ?? error ?? "Unable to estimate from this photo." },
    }));
  };
  const [carouselJumpCategory, setCarouselJumpCategory] = useState<string | null>(null);
  /** Toggles the LEFT-pane material section between grouped Summary and full BOM list. */
  const [materialView, setMaterialView] = useState<"summary" | "bom" | "scope">("summary");
  /** When set, workspace shows the Push-Mode "what's next" panel instead of the
   *  decision controls. Holds an optimistic post-approval job snapshot so
   *  getJobNextAction sees gates as cleared and surfaces Generate PO. */
  const [postApproveJob, setPostApproveJob] = useState<Job | null>(null);
  const { options: allOptions } = useProductOptions();
  const { products: registryProducts } = useProductRegistry();
  const checklistPersistSeqRef = useRef(0);
  // Note: reviewerNotes & JobActivityTimeline removed from this surface (2026-04-30)
  // — the right pane is now reserved for System Checks + Checklist + Decision so
  // the reviewer can complete approval without scrolling. Notes/timeline live
  // on Job Detail.


  const allChecked = useMemo(() => CHECKLIST_ITEMS.every(item => checklist[item.key]), [checklist]);
  const chainEnabled = !!(onApproveAndContinue && onContinueChain);

  const hydrateChecklist = (raw?: Record<string, boolean>) =>
    CHECKLIST_ITEMS.reduce<Record<string, boolean>>((acc, item) => {
      acc[item.key] = !!raw?.[item.key];
      return acc;
    }, {});


  useEffect(() => {
    if (!job || !open) return;
    // First hydrate from the in-memory job snapshot for instant render.
    const local = (job as Job & { buildReviewChecklist?: Record<string, boolean> }).buildReviewChecklist ?? {};
    setChecklist(hydrateChecklist(local));

    // Then reconcile from DB to avoid stale in-memory state after prior writes.
    let cancelled = false;
    void JobService.getById(String(job.id || job.jobNumber)).then(({ data, error }) => {
      if (cancelled || error || !data) return;
      setChecklist(hydrateChecklist(data.buildReviewChecklist));
    });
    return () => {
      cancelled = true;
    };
  }, [job?.id, job?.jobNumber, open]);

  useEffect(() => {
    if (!job?.id || !open) return;
    const seq = ++checklistPersistSeqRef.current;
    const timer = setTimeout(() => {
      void JobService.updateStage(job.id, { buildReviewChecklist: checklist }).then(({ error }) => {
        if (seq !== checklistPersistSeqRef.current) return;
        if (error) {
          console.warn("Failed to persist build review checklist:", error);
          toast.error("Failed to save checklist", { description: "Your review checks were not persisted." });
        }
      });
    }, 120);

    return () => {
      clearTimeout(timer);
    };
  }, [checklist, job?.id, open]);

  useEffect(() => {
    const rawRep = job?.salesRep?.trim() || "";
    if (!rawRep) {
      setSalesRepDisplayName("");
      return;
    }
    if (!isUuid(rawRep)) {
      setSalesRepDisplayName(rawRep);
      return;
    }
    let cancelled = false;
    supabase
      .from("profiles")
      .select("id, full_name, first_name, last_name, email")
      .eq("id", rawRep)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setSalesRepDisplayName(rawRep);
          return;
        }
        const fullName = data.full_name?.trim();
        const joinedName = [data.first_name, data.last_name].filter(Boolean).join(" ").trim();
        const emailPrefix = data.email?.split("@")[0]?.trim();
        setSalesRepDisplayName(fullName || joinedName || emailPrefix || rawRep);
      });
    return () => {
      cancelled = true;
    };
  }, [job?.salesRep]);

  // Load site photos from the linked quote (consultation) and migration import photos
  useEffect(() => {
    if (!job || !open) { setSitePhotos([]); return; }
    let cancelled = false;
    const loadConsultationPhotos = async (): Promise<SitePhoto[]> => {
      const quoteId = job.convertedFromQuoteId;
      if (!quoteId) return [];
      const records = await getAttachments("quote", quoteId).catch(() => []);
      const normalizeStoragePath = (rawPath: string, bucket: string): string => {
        if (!rawPath) return "";
        const privateMarker = `/storage/v1/object/sign/${bucket}/`;
        const publicMarker = `/storage/v1/object/public/${bucket}/`;
        if (rawPath.includes(privateMarker)) return rawPath.split(privateMarker)[1]?.split("?")[0] ?? "";
        if (rawPath.includes(publicMarker)) return rawPath.split(publicMarker)[1]?.split("?")[0] ?? "";
        return rawPath;
      };
      const siteRecords = records.filter(
        (r) => {
          const attachmentType = r.attachmentType;
          const notes = r.notes;
          return attachmentType === "photo" && typeof notes === "string" && notes.startsWith("site_photo:");
        }
      );
      return Promise.all(
        siteRecords.map(async (r) => {
          const rawPath = String(r.filePath);
          const storagePath = normalizeStoragePath(rawPath, SITE_PHOTOS_BUCKET);
          const preview = await createSignedAttachmentUrl(storagePath, SITE_PHOTOS_BUCKET).catch(() => rawPath);
          const notes = String(r.notes ?? "");
          return {
            id: r.id,
            category: notes.replace("site_photo:", ""),
            preview,
            name: String(r.fileName ?? r.file_name ?? "site-photo"),
            capturedAt: String(r.createdAt ?? r.created_at ?? new Date().toISOString()),
            source: "consultation" as const,
          } as SitePhoto;
        })
      );
    };

    const loadJobDirectPhotos = async (): Promise<SitePhoto[]> => {
      const records = await getAttachments("job", job.id).catch(() => []);
      const photoRecords = records.filter(
        (r) => r.attachmentType === "photo" && typeof r.notes === "string" &&
          (r.notes.startsWith("site_photo:") || r.notes.startsWith("job_photo"))
      );
      return Promise.all(
        photoRecords.map(async (r) => {
          const rawPath = String(r.filePath ?? "");
          const notes = String(r.notes ?? "");
          // job_photo uploads live in job-photos bucket; site_photo uploads live in job-documents
          const bucket = notes.startsWith("job_photo") ? "job-photos" : SITE_PHOTOS_BUCKET;
          const preview = await createSignedAttachmentUrl(rawPath, bucket).catch(() => rawPath);
          // Derive a category: site_photo:xxx → xxx, job_photo:groupId → groupId, job_photo → project_area
          let category = "project_area";
          if (notes.startsWith("site_photo:")) category = notes.replace("site_photo:", "") || "project_area";
          else if (notes.startsWith("job_photo:")) category = notes.replace("job_photo:", "") || "project_area";
          return {
            id: r.id,
            category,
            preview,
            name: String(r.fileName ?? r.file_name ?? "site-photo"),
            capturedAt: String(r.createdAt ?? r.created_at ?? new Date().toISOString()),
            source: "consultation" as const,
          } as SitePhoto;
        })
      );
    };

    const loadMigrationPhotos = async (): Promise<SitePhoto[]> => {
      const rawPhotos = Array.isArray(job.photos) ? job.photos : [];
      const groupRefs = Array.isArray(job.photoGroups)
        ? job.photoGroups.flatMap((g) => Array.isArray(g.photoRefs) ? g.photoRefs : [])
        : [];
      const allRefs = Array.from(new Set([...rawPhotos, ...groupRefs])).filter(Boolean);
      if (allRefs.length === 0) return [];
      const resolved = await resolveImportPhotoUrls(allRefs).catch(() => allRefs);
      return resolved.map((preview, idx) => ({
        id: `migration-photo-${job.id}-${idx}`,
        category: "imported",
        preview,
        name: `Imported photo ${idx + 1}`,
        capturedAt: new Date().toISOString(),
        source: "migration" as const,
      }));
    };

    Promise.all([loadConsultationPhotos(), loadJobDirectPhotos(), loadMigrationPhotos()])
      .then(([consultation, jobDirect, migration]) => {
        if (cancelled) return;
        const seen = new Set<string>();
        const merged: SitePhoto[] = [];
        for (const photo of [...consultation, ...jobDirect, ...migration]) {
          if (seen.has(photo.preview)) continue;
          seen.add(photo.preview);
          merged.push(photo);
        }
        setSitePhotos(merged);
      })
      .catch(() => { if (!cancelled) setSitePhotos([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, open]);

  // Derive live lane from build-review events for this job so the workspace
  // moves through Push-Mode handoffs (sent_to_sales → awaiting-sales →
  // awaiting-signature → final-review) without ever closing.
  const currentLane: ReviewLane = useMemo(() => {
    if (!job) return lane;
    const evts = (buildReviewEvents ?? bathReviewEvents ?? []).filter(e => e.jobId === job.id);
    if (evts.length === 0) return lane;
    const status: BuildReviewStatus = deriveBuildReviewStatus(evts);
    switch (status) {
      case "Needs Review":                       return "needs-review";
      case "On Hold — Sales Action Required":    return "on-hold-sales";
      case "Revision Required":                  return "revision-required";
      case "Awaiting Sales Confirmation":        return "awaiting-sales";
      case "Awaiting Customer Signature":        return "awaiting-signature";
      case "Resubmitted for Review":             return "resubmitted";
      case "Final Review":                       return "final-review";
      case "Approved":                           return lane; // post-terminal — keep last lane
      default:                                   return lane;
    }
  }, [job, buildReviewEvents, bathReviewEvents, lane]);

  const toggleCheck = (key: string) => setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));

  const buildOptimisticApproved = (j: Job): Job => ({
    ...j,
    scopeLocked: true,
    intakeApproved: true,
    approvedForOrdering: true,
    planningStatus: "In Planning",
    currentStage: "Review Complete",
  });

  const handleApprove = () => {
    if (!job) return;
    if (chainEnabled) {
      onApproveAndContinue!(job.id);
      setPostApproveJob(buildOptimisticApproved(job));
      return;
    }
    onApprove(job.id);
    resetState();
  };

  const handleFinalApproveClick = () => {
    if (!job) return;
    if (chainEnabled) {
      onApproveAndContinue!(job.id);
      setPostApproveJob(buildOptimisticApproved(job));
      return;
    }
    onFinalApproval?.(job.id);
    resetState();
  };

  const handleRevisionSubmit = () => {
    if (!job || !revisionNotes.trim() || !revisionReason) return;
    onRequestRevision(job.id, revisionNotes, revisionMaterials, revisionReason as RevisionReason);
    resetState();
  };

  const handleFlagPhoto = (photoIndex: number, category: string) => {
    setRevisionReason("photo_insufficient" as RevisionReason);
    setRevisionNotes(`Photo ${photoIndex + 1} — ${category}: needs clarification`);
    setShowRevisionForm(true);
  };

  const resetState = () => {
    setChecklist({});
    setRevisionNotes("");
    setRevisionMaterials("");
    setRevisionReason("");
    setShowRevisionForm(false);
    setExpandedPhoto(null);
    setSitePhotos([]);
    setLightboxIndex(null);
    setMaterialView("summary");
    setPostApproveJob(null);
    setMobileTab("photos");
    onClose();
  };

  const handleChainContinue = (step: PushNextStep) => {
    if (!postApproveJob || !onContinueChain) return;
    const target = postApproveJob;
    setPostApproveJob(null);
    onContinueChain(target, step);
    // Reset internal form state but DON'T re-call onClose — the parent owns the
    // close lifecycle once a chained step fires (it may keep the workspace
    // closed and open a different modal like OrderingModal).
    setChecklist({});
    setRevisionNotes("");
    setRevisionMaterials("");
    setRevisionReason("");
    setShowRevisionForm(false);
    setExpandedPhoto(null);
    setSitePhotos([]);
    setLightboxIndex(null);
    setMaterialView("summary");
    onClose();
  };

  if (!job) return null;

  const registryNameBySku = new Map(
    registryProducts
      .map((product) => [String(product.sku || "").trim(), String(product.productName || "").trim()] as const)
      .filter(([sku, name]) => sku.length > 0 && name.length > 0),
  );
  const registryNameByProductId = new Map(
    registryProducts
      .map((product) => [String(product.productId || "").trim(), String(product.productName || "").trim()] as const)
      .filter(([id, name]) => id.length > 0 && name.length > 0),
  );

  const resolveMaterialDisplayName = (itemName: string, sku: string): string => {
    const rawName = String(itemName || "").trim();
    if (rawName && !isUuid(rawName)) return rawName;
    const rawSku = String(sku || "").trim();
    if (rawSku) {
      const bySku = registryNameBySku.get(rawSku);
      if (bySku) return bySku;
    }
    if (rawName) {
      const byProductId = registryNameByProductId.get(rawName);
      if (byProductId) return byProductId;
    }
    if (rawSku && !isUuid(rawSku)) return rawSku;
    return "Unmapped Product";
  };

  const cfg = job.projectConfig;
  const buildPackage = deriveBuildPackage(job, allOptions);
  const materialBomRows = (job.materialsList || [])
    .filter((row) => row && String(row.itemName || row.sku || "").trim().length > 0)
    .map((row) => ({
      sku: String(row.sku || "").trim() || "—",
      itemName: resolveMaterialDisplayName(
        String(row.itemName || row.sku || "").trim(),
        String(row.sku || "").trim(),
      ),
      quantity: Math.max(1, Number(row.quantityRequired ?? 1)),
      isAutoBundle: String(row.inventoryAssignment || "").trim().toLowerCase() === "auto bundle",
    }));
  const materialBomSourceLabel = materialBomRows.length > 0 ? "DB Materials List" : "POS Work Order";
  const validationIssues = deriveValidationIssues(job, allOptions);
  const hasErrors = !job.migrationImportDetail && validationIssues.some(i => i.type === "error");
  const hasWarnings = !job.migrationImportDetail && validationIssues.some(i => i.type === "warning");
  const photos: string[] = Array.isArray(job.photos)
    ? job.photos.filter((p): p is string => typeof p === "string" && p.trim().length > 0)
    : [];
  const scopeSnapshot = getScopeSnapshotLine(job);
  const derivedType = cfg?.autoProjectType || job.projectType;

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) resetState(); }}>
        <DialogContent stageSwap className="p-0 gap-0 [&>button]:hidden max-w-[min(1280px,95vw)] w-[95vw] h-[92vh] max-sm:max-w-none max-sm:w-screen max-sm:h-[100dvh] max-sm:rounded-none flex flex-col overflow-hidden">

          {/* ═══ TOP BAR — Job Summary ═══ */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card shrink-0">
            <div className="flex items-center gap-4">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Eye className="h-4.5 w-4.5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-[15px] font-bold text-foreground flex items-center gap-2">
                  {job.customerName}
                  <Badge variant="outline" className="text-[9px] h-4">{getJobDisplayRef(job)}</Badge>
                  <a href={`tel:${job.phone}`} className="text-primary hover:underline text-[12px] font-medium flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {job.phone}
                  </a>
                </DialogTitle>
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1 truncate max-w-[280px] sm:max-w-none"><MapPin className="h-3 w-3 shrink-0" /> {job.address.street}, {job.address.city}, {job.address.state} {job.address.zip}</span>
                  <Badge variant="outline" className="text-[9px] h-4 shrink-0">{derivedType}</Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Sales rep contact — moved here to fill the right-side dead
                  space and give the reviewer a one-click way to reach the rep
                  without leaving the workspace. */}
              {(salesRepDisplayName || job.salesRep) && (() => {
                const repName = salesRepDisplayName || job.salesRep || "";
                const repProfile = getRepByName(repName);
                const repPhone = repProfile?.phone;
                return (
                  <div className="hidden md:flex items-center gap-2 pr-3 border-r border-border">
                    <div className="h-7 w-7 rounded-full bg-muted/40 flex items-center justify-center">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="leading-tight text-right">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Sales Rep</p>
                      <p className="text-[12px] font-bold text-foreground">{repName}</p>
                      {repPhone ? (
                        <a href={`tel:${repPhone}`} className="text-[11px] font-medium text-primary hover:underline flex items-center gap-1 justify-end">
                          <Phone className="h-3 w-3" /> {repPhone}
                        </a>
                      ) : (
                        <span className="text-[10px] text-muted-foreground/60">No phone on file</span>
                      )}
                    </div>
                  </div>
                );
              })()}
              {hasErrors && (
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-destructive/15 text-destructive">Issues Found</span>
              )}
              {!hasErrors && hasWarnings && (
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-warning/15 text-warning">Warnings</span>
              )}
              {!hasErrors && !hasWarnings && (
                <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-success/15 text-success">Package Valid</span>
              )}
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={resetState}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Mobile tab bar — only visible below md breakpoint */}
          <div className="md:hidden flex items-center border-b border-border bg-card shrink-0">
            {(["photos", "spec", "review"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setMobileTab(tab)}
                className={cn(
                  "flex-1 py-2.5 text-[11px] font-semibold transition-colors border-b-2",
                  mobileTab === tab
                    ? "text-primary border-primary"
                    : "text-muted-foreground border-transparent"
                )}
              >
                {tab === "photos" ? "Photos" : tab === "spec" ? "Build Spec" : "Review"}
              </button>
            ))}
          </div>

          {/* ═══ 3-COLUMN LAYOUT ═══
              LEFT  (32%) — Measurements + Material Package (Summary | BOM toggle)
              CENTER(40%) — Scope + large Photo Gallery (review-grade tiles)
              RIGHT (28%) — System Checks + Review Checklist + Decision (no-scroll)
              Standalone "Notes & Conditions" pane removed (2026-04-30) — covered
              by measurement notes + scope chips on this surface. */}
          <div className="flex flex-1 min-h-0">

            {/* ═══ LEFT COLUMN — Measurements + Material Package ═══ */}
            <ScrollArea className={cn("border-r border-border h-full", mobileTab === "spec" ? "w-full md:w-[32%]" : "hidden md:block md:w-[32%]")}>
              <div className="p-4 space-y-4">

                {/* ── Measurement Snapshot — top of left column for side-by-side review ── */}
                <section className="space-y-1.5">
                  <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Ruler className="h-3.5 w-3.5" />
                    Measurement Snapshot
                  </h3>
                  {cfg ? (
                    <div className="rounded-lg border border-border bg-card divide-y divide-border/60">
                      {/* Top: layout + footprint condensed into a single 2-col row */}
                      <div className="grid grid-cols-2 gap-px bg-border/60">
                        <div className="bg-card p-2">
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Layout</p>
                          <p className="text-[11px] font-bold text-foreground leading-tight truncate">
                            {cfg.layoutType ? `${cfg.layoutType} • ${cfg.wallCount || 3}w` : "—"}
                          </p>
                        </div>
                        <div className="bg-card p-2">
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Footprint</p>
                          <p className="text-[11px] font-bold text-foreground leading-tight">
                            {cfg.baseWidth && cfg.baseLength ? `${cfg.baseWidth}" × ${cfg.baseLength}"` : "—"}
                          </p>
                        </div>
                      </div>

                      {/* Walls — single line per wall */}
                      {cfg.wallMeasurements && (
                        <div className="px-2 py-1.5 space-y-0.5">
                          {[
                            { label: "A", data: cfg.wallMeasurements.wallA },
                            { label: "B", data: cfg.wallMeasurements.wallB },
                            ...((cfg.wallCount || 3) >= 3 ? [{ label: "C", data: cfg.wallMeasurements.wallC }] : []),
                          ].map(wall => (
                            <div key={wall.label} className="flex items-center justify-between text-[10px]">
                              <span className="text-muted-foreground">Wall {wall.label}</span>
                              <span className="font-semibold tabular-nums text-foreground">
                                {wall.data?.width && wall.data?.height
                                  ? `${wall.data.width}" × ${wall.data.height}"`
                                  : <span className="text-warning">—</span>}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Drain + structural changes inline as chips */}
                      {(cfg.drainLocation || cfg.hasWindow || cfg.obstructions?.length || cfg.structuralChanges) && (
                        <div className="px-2 py-1.5 flex flex-wrap gap-1">
                          {cfg.drainLocation && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground font-medium">Drain: {cfg.drainLocation}</span>
                          )}
                          {cfg.hasWindow && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-warning/10 text-warning font-medium">Window in space</span>
                          )}
                          {cfg.obstructions?.slice(0, 2).map((o) => (
                            <span key={o} className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground font-medium">{o}</span>
                          ))}
                          {cfg.structuralChanges && Object.entries(cfg.structuralChanges)
                            .filter(([, v]) => v === true || v === "Yes")
                            .slice(0, 3)
                            .map(([k]) => (
                              <span key={k} className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium capitalize">
                                {k.replace(/([A-Z])/g, " $1").trim()}
                              </span>
                            ))}
                        </div>
                      )}

                      {/* Notes */}
                      {cfg.measurementNotes && (
                        <div className={`px-2 py-1.5 ${cfg.measurementNotes.includes("MISMATCH") ? "bg-destructive/[0.04]" : ""}`}>
                          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Notes</p>
                          <p className="text-[10px] text-foreground leading-snug">{cfg.measurementNotes}</p>
                        </div>
                      )}
                    </div>
                  ) : (() => {
                    const aiDraft = job.migrationImportDetail?.aiScopeDraft;
                    if (!aiDraft) return (
                      <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-warning/5 border border-warning/20">
                        <AlertTriangle className="h-3 w-3 text-warning shrink-0" />
                        <span className="text-[10px] text-warning font-medium">No measurements recorded</span>
                      </div>
                    );
                    // Extract dimension-like items from panelBreakdown (e.g. "30"–36" × 60"")
                    const dimPattern = /\d+[""']?\s*[×x–-]\s*\d+/i;
                    const panelDims = (aiDraft.panelBreakdown ?? []).filter(s => dimPattern.test(s));
                    const matDims = (aiDraft.materialsAndSelections ?? []).filter(s => dimPattern.test(s));
                    const allDims = [...panelDims, ...matDims];
                    return (
                      <div className="rounded-lg border border-border bg-card divide-y divide-border/60">
                        {aiDraft.workArea && (
                          <div className="px-2 py-1.5">
                            <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Work Area</p>
                            <p className="text-[11px] font-bold text-foreground leading-snug">{aiDraft.workArea}</p>
                          </div>
                        )}
                        {aiDraft.projectType && (
                          <div className="px-2 py-1.5">
                            <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Project Type</p>
                            <p className="text-[11px] font-semibold text-foreground">{aiDraft.projectType}</p>
                          </div>
                        )}
                        {allDims.length > 0 && (
                          <div className="px-2 py-1.5 space-y-0.5">
                            <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Extracted Dimensions</p>
                            {allDims.map((d, i) => (
                              <p key={i} className="text-[10px] text-foreground font-medium tabular-nums">{d}</p>
                            ))}
                          </div>
                        )}
                        <div className="px-2 py-1.5 flex items-center gap-1">
                          <Sparkles className="h-2.5 w-2.5 text-muted-foreground/40" />
                          <span className="text-[9px] text-muted-foreground/40">From AI contract scope · {Math.round((aiDraft.confidence ?? 0) * 100)}% confidence</span>
                        </div>
                      </div>
                    );
                  })()}
                </section>

                {/* ── Build Spec (Summary | BOM toggle) — top-aligned ── */}
                <section className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Package className="h-3.5 w-3.5" />
                      Build Spec
                      <span className="text-[10px] font-medium normal-case tracking-normal">
                        ({materialView === "summary" ? buildPackage.length : (materialBomRows.length > 0 ? materialBomRows.length : buildPackage.length)})
                      </span>
                    </h3>
                  </div>

                  {/* Summary | BOM | Scope toggle */}
                  <div role="tablist" aria-label="Build spec view" className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/20 p-0.5 flex-wrap">
                    {(["summary", "bom", ...(job.migrationImportDetail?.aiScopeDraft?.scopeSummary ? ["scope"] : [])] as const).map(v => (
                      <button
                        key={v}
                        role="tab"
                        aria-selected={materialView === v}
                        onClick={() => setMaterialView(v as "summary" | "bom" | "scope")}
                        className={cn(
                          "px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors",
                          materialView === v
                            ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {v === "summary" ? "Summary" : v === "bom" ? "BOM" : "AI Scope"}
                      </button>
                    ))}
                    {materialView !== "scope" && (
                      <span className="text-[9px] text-muted-foreground/70 px-2 py-0.5 rounded bg-muted/40">Source: {materialBomSourceLabel}</span>
                    )}
                  </div>

                  {/* Summary view — native build package, or AI-derived cards for imported jobs */}
                  {materialView === "summary" && (() => {
                    const aiDraft = job.migrationImportDetail?.aiScopeDraft;
                    const displayItems = buildPackage.length > 0
                      ? buildPackage
                      : aiDraft ? deriveAiScopeSummaryCards(aiDraft) : [];
                    const isAiDerived = buildPackage.length === 0 && !!aiDraft;
                    return displayItems.length > 0 ? (
                      <div className="space-y-1.5">
                        {isAiDerived && (
                          <p className="text-[9px] text-muted-foreground/50 flex items-center gap-1">
                            <Sparkles className="h-2.5 w-2.5" /> Derived from AI contract scope
                          </p>
                        )}
                        {displayItems.map(item => {
                          const preferredCategory = (
                            item.category === "Wall System" || item.category === "Panel Breakdown" || item.category === "Measurement Reference"
                              ? "measurement"
                              : item.category === "Obstruction" || item.category === "Field Notes"
                                ? "obstruction"
                                : "project_area"
                          );
                          const photoCategory = sitePhotos.some(p => p.category === preferredCategory)
                            ? preferredCategory
                            : sitePhotos.length > 0 ? sitePhotos[0].category : null;
                          const hasPhotos = !!photoCategory;
                          return (
                            <button
                              key={item.category}
                              onClick={() => { if (hasPhotos && photoCategory) setCarouselJumpCategory(photoCategory); }}
                              className={cn(
                                "w-full flex items-start gap-2.5 px-3 py-2.5 rounded-lg border bg-card text-left transition-colors",
                                hasPhotos
                                  ? "border-border hover:border-primary/40 hover:bg-primary/[0.02] cursor-pointer"
                                  : "border-border cursor-default"
                              )}
                            >
                              <div className="h-7 w-7 rounded-md bg-muted/50 flex items-center justify-center shrink-0 text-muted-foreground mt-0.5">
                                {item.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">{item.category}</p>
                                <p className="text-[12px] font-semibold text-foreground mt-0.5 line-clamp-2">{item.value}</p>
                                {item.measurement && (
                                  <p className="text-[10px] text-primary font-medium mt-0.5">{item.measurement}</p>
                                )}
                              </div>
                              {hasPhotos && <span className="text-[8px] text-primary/50 mt-1 shrink-0">→ photos</span>}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">No build configuration available</p>
                    );
                  })()}

                  {/* BOM view — DB-backed materials list when available */}
                  {materialView === "bom" && (
                    <div className="rounded-lg border border-border overflow-hidden">
                      {/* Measurement context header for BOM */}
                      {cfg && (
                        <div className="border-b border-border">
                          <div className="grid grid-cols-2 gap-px bg-border/60">
                            <div className="bg-card p-2">
                              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Layout</p>
                              <p className="text-[11px] font-bold text-foreground leading-tight truncate">
                                {cfg.layoutType ? `${cfg.layoutType} • ${cfg.wallCount || 3}w` : "—"}
                              </p>
                            </div>
                            <div className="bg-card p-2">
                              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Footprint</p>
                              <p className="text-[11px] font-bold text-foreground leading-tight">
                                {cfg.baseWidth && cfg.baseLength ? `${cfg.baseWidth}" × ${cfg.baseLength}"` : "—"}
                              </p>
                            </div>
                          </div>
                          {cfg.wallMeasurements && (
                            <div className="grid grid-cols-3 gap-px bg-border/60">
                              {[
                                { label: "Wall A", data: cfg.wallMeasurements.wallA },
                                { label: "Wall B", data: cfg.wallMeasurements.wallB },
                                ...((cfg.wallCount || 3) >= 3 ? [{ label: "Wall C", data: cfg.wallMeasurements.wallC }] : []),
                              ].map(wall => (
                                <div key={wall.label} className="bg-card p-2">
                                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{wall.label}</p>
                                  <p className="text-[10px] font-semibold tabular-nums text-foreground leading-tight">
                                    {wall.data?.width && wall.data?.height
                                      ? `${wall.data.width}" × ${wall.data.height}"`
                                      : "—"}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-[1fr_70px_44px] text-[9px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/30 px-3 py-1.5 border-b">
                        <span>Item</span>
                        <span className="text-right">SKU</span>
                        <span className="text-right">Qty</span>
                      </div>
                      {(materialBomRows.length > 0 ? (() => {
                        const baseRows = materialBomRows
                          .filter((item) => !item.isAutoBundle)
                          .map((item) => ({
                            kind: "row" as const,
                            item: item.itemName,
                            sku: item.sku,
                            qty: item.quantity,
                          }));
                        const autoBundleRows = materialBomRows
                          .filter((item) => item.isAutoBundle)
                          .map((item) => ({
                            kind: "row" as const,
                            item: item.itemName,
                            sku: item.sku,
                            qty: item.quantity,
                          }));
                        if (autoBundleRows.length === 0) return baseRows;
                        return [
                          ...baseRows,
                          { kind: "group" as const, label: "Auto-Bundled Supplies" },
                          ...autoBundleRows,
                        ];
                      })() : buildPackage.map((item) => ({
                        kind: "row" as const,
                        item: item.value,
                        sku: `${item.category.substring(0, 3).toUpperCase()}-001`,
                        qty: 1,
                      }))).map((row, idx) => (
                        row.kind === "group" ? (
                          <div key={`group-${idx}`} className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/20 border-y border-border">
                            {row.label}
                          </div>
                        ) : (
                          <div key={idx} className="grid grid-cols-[1fr_70px_44px] px-3 py-2 text-[11px] border-b border-border last:border-b-0">
                            <span className="font-medium text-foreground truncate" title={row.item}>{row.item}</span>
                            <span className="text-right text-muted-foreground font-mono text-[9px]">{row.sku}</span>
                            <span className="text-right font-medium">{row.qty}</span>
                          </div>
                        )
                      ))}
                    </div>
                  )}

                  {/* AI Scope tab — imported jobs only */}
                  {materialView === "scope" && job.migrationImportDetail?.aiScopeDraft && (
                    <AiScopeCard draft={job.migrationImportDetail.aiScopeDraft} />
                  )}
                </section>
              </div>
            </ScrollArea>

            {/* ═══ CENTER COLUMN — Scope + Photo Carousel ═══ */}
            <div className={cn("border-r border-border h-full flex-col overflow-hidden", mobileTab === "photos" ? "flex w-full md:w-[40%]" : "hidden md:flex md:w-[40%]")}>

              {/* Scope Snapshot — native jobs only; imported jobs show AI Scope in left column */}
              {!job.migrationImportDetail?.aiScopeDraft?.scopeSummary && (
                <section className="shrink-0 px-4 pt-4">
                  <Card className="border-primary/20 bg-primary/[0.02] overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Scope Snapshot</h3>
                      </div>
                      <p className="text-[16px] font-bold text-foreground leading-snug">{scopeSnapshot}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-[9px] h-4">{derivedType}</Badge>
                        {cfg?.laborComplexity && cfg.laborComplexity !== "standard" && (
                          <Badge variant="outline" className="text-[9px] h-4 border-warning/30 text-warning capitalize">{cfg.laborComplexity} complexity</Badge>
                        )}
                        {job.approvedForOrdering && <Badge className="text-[9px] h-4 bg-success/15 text-success border-0">Materials Ready</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                </section>
              )}

              {/* ── Site Photos — unified carousel ── */}
              <section className="shrink-0 px-4 pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Camera className="h-3.5 w-3.5" />
                    Site Photos
                    <span className="text-[10px] font-medium normal-case tracking-normal">({sitePhotos.length})</span>
                  </h3>
                  {!job.convertedFromQuoteId && !job.migrationImportDetail && (
                    <span className="text-[9px] text-muted-foreground">No linked quote</span>
                  )}
                </div>
                <SitePhotoCarousel
                  photos={sitePhotos}
                  onFlag={handleFlagPhoto}
                  onExpand={(idx) => setLightboxIndex(idx)}
                  jumpToCategory={carouselJumpCategory}
                  onJumpHandled={() => setCarouselJumpCategory(null)}
                  estimations={photoEstimations}
                  onEstimate={handleEstimatePhoto}
                />
              </section>

              {/* ── 2D Floor Plan — fills remaining vertical space ── */}
              {(() => {
                // Native job — use real measurements
                if (cfg?.baseWidth && cfg?.baseLength) {
                  return (
                    <div className="flex-1 min-h-0 flex flex-col px-4 py-3">
                      <ShowerFloorPlan cfg={cfg} />
                    </div>
                  );
                }
                // Native job fallback — derive base dimensions from wall measurements
                // Wall A width = back wall (wider), Wall B width = side wall depth (narrower)
                const wm = cfg?.wallMeasurements;
                if (wm?.wallA?.width && wm?.wallB?.width) {
                  const derivedCfg = {
                    ...cfg,
                    baseWidth: Math.max(wm.wallA.width, wm.wallB.width),
                    baseLength: Math.min(wm.wallA.width, wm.wallB.width),
                  } as NonNullable<Job["projectConfig"]>;
                  return (
                    <div className="flex-1 min-h-0 flex flex-col px-4 py-3">
                      <ShowerFloorPlan cfg={derivedCfg} approximate />
                    </div>
                  );
                }
                // Imported job — try to parse base dimensions from AI scope text
                const aiDraft = job.migrationImportDetail?.aiScopeDraft;
                if (!aiDraft) return null;
                const dimRe = /(\d{2,3})[""']?\s*[×xX]\s*(\d{2,3})[""']?/;
                const sources = [
                  ...(aiDraft.panelBreakdown ?? []),
                  ...(aiDraft.materialsAndSelections ?? []),
                  ...(aiDraft.extractedMaterials?.map(m => m.itemName) ?? []),
                  aiDraft.scopeSummary ?? "",
                ];
                let parsedW: number | null = null;
                let parsedL: number | null = null;
                for (const s of sources) {
                  const m = s.match(dimRe);
                  if (m) {
                    parsedW = Number(m[1]);
                    parsedL = Number(m[2]);
                    break;
                  }
                }
                if (!parsedW || !parsedL) return null;
                // Wall A = back/main wall = always the wider dimension (e.g. 60").
                // Wall B/C = side walls = always the narrower dimension (e.g. 36").
                // AI scope text may list either order (e.g. "36 × 60" or "60 × 36"),
                // so we always assign max → baseWidth (Wall A) and min → baseLength (sides).
                const approxCfg = {
                  baseWidth: Math.max(parsedW, parsedL),
                  baseLength: Math.min(parsedW, parsedL),
                } as NonNullable<Job["projectConfig"]>;
                return (
                  <div className="flex-1 min-h-0 flex flex-col px-4 py-3">
                    <ShowerFloorPlan cfg={approxCfg} approximate />
                  </div>
                );
              })()}
            </div>

            {/* ═══ RIGHT COLUMN — System Checks + Checklist + Decision ═══
                Reviewer Notes & Activity Timeline removed (2026-04-30) so the
                checklist + approval controls fit on one screen without scrolling. */}
            <ScrollArea className={cn("bg-muted/[0.015] h-full", mobileTab === "review" ? "w-full md:w-[28%]" : "hidden md:block md:w-[28%]")}>
              <div className="p-4 space-y-3">

                {/* System Validation Checks — native jobs only; migrated jobs lack projectConfig */}
                {!job.migrationImportDetail && <section className="space-y-2">
                  <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    System Checks
                  </h3>
                  {validationIssues.length > 0 ? (
                    <div className="space-y-1.5">
                      {validationIssues.map((issue, i) => (
                        <div key={i} className={`flex items-start gap-2 px-2.5 py-2 rounded-lg border text-[10px] font-medium ${
                          issue.type === "error"
                            ? "border-destructive/20 bg-destructive/[0.04] text-destructive"
                            : "border-warning/20 bg-warning/[0.04] text-warning"
                        }`}>
                          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                          <span>{issue.message}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-success/20 bg-success/[0.04] text-[10px] font-medium text-success">
                      <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
                      All checks passed — package valid
                    </div>
                  )}
                </section>}

                {/* Validation Checklist */}
                <section className="space-y-2">
                  <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5" />
                    Review Checklist
                  </h3>
                  <div className="space-y-1.5">
                    {CHECKLIST_ITEMS.map(item => (
                      <button key={item.key} type="button" onClick={() => toggleCheck(item.key)}
                        className={`flex items-center gap-2 w-full px-2.5 py-2 rounded-lg border transition-all active:scale-[0.98] text-left ${
                          checklist[item.key] ? "border-success/30 bg-success/5" : "border-border hover:border-primary/30"
                        }`}>
                        <div className={`shrink-0 h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                          checklist[item.key] ? "bg-success border-success" : "border-muted-foreground/30"
                        }`}>
                          {checklist[item.key] && <CheckCircle className="h-2.5 w-2.5 text-success-foreground" />}
                        </div>
                        <span className={`text-[11px] font-medium ${checklist[item.key] ? "text-foreground" : "text-muted-foreground"}`}>
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 pt-0.5">
                    <ShieldCheck className={`h-3.5 w-3.5 ${allChecked ? "text-success" : "text-muted-foreground/30"}`} />
                    <span className={`text-[10px] font-semibold ${allChecked ? "text-success" : "text-muted-foreground/50"}`}>
                      {allChecked ? "All checks complete" : `${Object.values(checklist).filter(Boolean).length} / ${CHECKLIST_ITEMS.length}`}
                    </span>
                  </div>
                </section>

                {/* ═══ Decision Controls — swap to Push next-step panel after approval ═══ */}
                {postApproveJob && chainEnabled ? (
                  <section className="space-y-2 pt-2 border-t border-border">
                    <PushNextStepPanel
                      job={postApproveJob}
                      openIssues={openIssues}
                      headline="Build review approved"
                      summary="Pre-spend gate updated. Pick the next step to keep this job moving."
                      autoArm
                      onContinue={(step) => handleChainContinue(step)}
                      onPause={resetState}
                    />
                  </section>
                ) : (
                <section className="space-y-2 pt-2 border-t border-border">
                  {(currentLane === "needs-review" || currentLane === "resubmitted") && (
                    <>
                      {!showRevisionForm ? (
                        <>
                          <Button className="w-full h-9 text-[11px] font-bold gap-1.5" disabled={!allChecked} onClick={handleApprove}>
                            <CheckCircle className="h-3.5 w-3.5" />
                            {allChecked ? "Approve — Send to Planning" : "Complete checklist to approve"}
                          </Button>
                          <Button variant="outline"
                            className="w-full h-9 text-[11px] font-semibold gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/5"
                            onClick={() => setShowRevisionForm(true)}>
                            <RotateCcw className="h-3.5 w-3.5" />
                            Request Revision
                          </Button>
                        </>
                      ) : (
                        <div className="space-y-2.5 p-3 rounded-lg border border-destructive/20 bg-destructive/[0.02]">
                          <h4 className="text-[11px] font-bold text-destructive flex items-center gap-1.5">
                            <RotateCcw className="h-3.5 w-3.5" /> Request Revision
                          </h4>
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-semibold text-foreground">Reason *</label>
                            <Select value={revisionReason} onValueChange={v => setRevisionReason(v as RevisionReason)}>
                              <SelectTrigger className="h-8 text-[11px]"><SelectValue placeholder="Select..." /></SelectTrigger>
                              <SelectContent>
                                {(Object.entries(REVISION_REASON_LABELS) as [RevisionReason, string][]).map(([k, v]) => (
                                  <SelectItem key={k} value={k} className="text-[11px]">{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-semibold text-foreground">Notes *</label>
                            <Textarea value={revisionNotes} onChange={e => setRevisionNotes(e.target.value)}
                              placeholder="What needs correction..." className="text-[11px] min-h-[60px] bg-card" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[10px] font-semibold text-foreground">Material Changes</label>
                            <Textarea value={revisionMaterials} onChange={e => setRevisionMaterials(e.target.value)}
                              placeholder="Optional material corrections..." className="text-[11px] min-h-[40px] bg-card" />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" className="h-7 text-[10px] flex-1"
                              onClick={() => { setShowRevisionForm(false); setRevisionNotes(""); setRevisionMaterials(""); setRevisionReason(""); }}>
                              Cancel
                            </Button>
                            <Button className="h-7 text-[10px] font-semibold flex-1 gap-1"
                              disabled={!revisionNotes.trim() || !revisionReason}
                              onClick={handleRevisionSubmit}>
                              <RotateCcw className="h-3 w-3" /> Submit
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {(currentLane === "on-hold-sales" || currentLane === "revision-required") && (
                    <Button className="w-full h-9 text-[11px] font-bold gap-1.5"
                      onClick={() => { if (job) onSendToSales?.(job.id); }}>
                      <Send className="h-3.5 w-3.5" /> Send to Sales for Confirmation
                    </Button>
                  )}

                  {currentLane === "awaiting-sales" && (
                    <Button className="w-full h-9 text-[11px] font-bold gap-1.5"
                      onClick={() => { if (job) onSalesApprove?.(job.id); }}>
                      <CheckCircle className="h-3.5 w-3.5" /> Sales Approve & Create Change Order
                    </Button>
                  )}

                  {currentLane === "awaiting-signature" && (
                    <Button className="w-full h-9 text-[11px] font-bold gap-1.5"
                      onClick={() => { if (job) onCustomerSigned?.(job.id); }}>
                      <FileSignature className="h-3.5 w-3.5" /> Mark as Customer Signed
                    </Button>
                  )}

                   {currentLane === "final-review" && (
                    <Button className="w-full h-9 text-[11px] font-bold gap-1.5 bg-success text-success-foreground hover:bg-success/90"
                      disabled={!allChecked}
                      onClick={handleFinalApproveClick}>
                      <CheckCircle className="h-3.5 w-3.5" />
                      {allChecked ? "Final Approve — Send to Planning" : "Complete checklist"}
                    </Button>
                  )}
                </section>
                )}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>


      {/* Site Photo Lightbox — zoom/pan/navigate */}
      <ZoomPanLightbox
        photos={sitePhotos}
        initialIndex={lightboxIndex ?? 0}
        open={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        wallMeasurements={cfg?.wallMeasurements}
        baseWidth={cfg?.baseWidth}
        baseLength={cfg?.baseLength}
        estimations={photoEstimations}
        onEstimate={handleEstimatePhoto}
      />
    </>
  );
});
